import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shownAssignments } from './shownAssignments';
import { shiftDateLabel } from './shiftWeekday';

// Lifted verbatim out of Dashboard.js. Prop names match the variables it used to
// close over, so the markup below is unchanged from what it was inline.
const AssignmentsSection = ({
  elections,
  handleConfirmShift,
  handleRejectShift,
  assignmentConfirmLoading,
  assignmentConfirmMessage,
  assignmentRejectLoading,
  assignmentRejectMessage,
}) => {
  // The set a DJ tapped Reject on, waiting for them to say they're sure. Rejecting
  // can't be undone from the hub, so nothing is saved until they confirm. Only the rid
  // is kept: the set is looked up again when they answer, so a confirm that finished
  // while the question was open isn't overwritten with a stale copy.
  const [rejectingRid, setRejectingRid] = useState(null);
  const openedFrom = useRef(null);
  const assignments = elections[0]?.dj_elections?.[0]?.active_assignments || [];
  // Rejected and inactive sets are not shown at all.
  const shown = shownAssignments(assignments);
  const rejectingAssignment = shown.find((a) => a.rid === rejectingRid && !a.dj_confirmation_shift);
  // Sets rejected on this page: their cards are gone, so say so above the rest.
  const justRejected = assignments.filter((a) => a.dj_rejected_shift && assignmentRejectMessage[a.rid]);

  const askToReject = (assignment) => {
    openedFrom.current = document.activeElement;
    setRejectingRid(assignment.rid);
  };

  const closeRejectDialog = useCallback(() => {
    setRejectingRid(null);
    openedFrom.current?.focus?.();
  }, []);

  const confirmReject = () => {
    const assignment = rejectingAssignment;
    setRejectingRid(null);
    if (assignment) handleRejectShift(assignment);
  };

  useEffect(() => {
    if (!rejectingAssignment) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') closeRejectDialog(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [rejectingAssignment, closeRejectDialog]);

  return (
    <div id="assignments-section" className="bg-white rounded-xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-gray-900">
          Polling Place Assignments
        </h3>
      </div>

      <div className="space-y-4">
        {justRejected.map((assignment) => (
          <p key={assignment.rid} role="status" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            Your {assignment.shift} set on {assignment.shift_date} was rejected.
          </p>
        ))}
        {shown.length > 0 && !elections[0]?.dj_elections?.[0]?.dont_display_assignments ? (
          <div className="space-y-6">
            {/* Desktop view (Card style, hidden on mobile) */}
            <div className="hidden md:block space-y-4">
              {shown.map((assignment) => (
                <div
                  key={assignment.rid}
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                >
                  {/* Top Row: Set badge and status badge */}
                  <div className="flex justify-between items-center mb-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        assignment.shift === 'Morning'
                          ? 'bg-yellow-100 text-yellow-800'
                          : assignment.shift === 'Afternoon'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                    {shiftDateLabel(assignment.shift_date)}, {assignment.shift}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        assignment.dj_confirmation_shift
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {assignment.dj_confirmation_shift ? 'Confirmed' : 'Needs Confirmation'}
                    </span>
                  </div>

                  {/* Polling place */}
                  <div className="mb-3">
                    <p className="text-xs text-gray-900 font-medium mb-1">Polling Place</p>
                    <p className="text-sm text-gray-700">{assignment.polling_place_address}</p>
                  </div>

                  {/* Time */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-900 font-medium mb-1">Time</p>
                    <p className="text-sm text-gray-700">{assignment.shift_start_time} - {assignment.shift_end_time}</p>
                  </div>

                  {/* Action Buttons or Status Text */}
                  {!assignment.dj_confirmation_shift ? (
                    <div className="flex gap-2">
                      <button
                      onClick={() => handleConfirmShift(assignment)}
                      disabled={assignmentConfirmLoading[assignment.rid]}
                      className={`inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded shadow-sm text-white ${
                        assignmentConfirmLoading[assignment.rid]
                          ? 'bg-gray-400'
                          : 'bg-primary hover:bg-primary-dark'
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                    >
                      {assignmentConfirmLoading[assignment.rid] ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Confirming...
                        </>
                      ) : (
                        'Confirm'
                      )}
                      </button>
                      <button
                        onClick={() => askToReject(assignment)}
                        disabled={assignmentRejectLoading[assignment.rid]}
                        className={`inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded shadow-sm ${
                          assignmentRejectLoading[assignment.rid]
                            ? 'bg-gray-100 text-gray-400'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                      >
                        {assignmentRejectLoading[assignment.rid] ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Rejecting...
                          </>
                        ) : (
                          "Reject"
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="text-green-600 text-sm font-medium mt-1">
                      ✓ Set Confirmed
                    </div>
                  )}

                  {/* Confirmation message feedback */}
                  {assignmentConfirmMessage[assignment.rid] && (
                    <p
                      className={`text-xs mt-2 ${
                        assignmentConfirmMessage[assignment.rid].includes('Failed')
                          ? 'text-red-600'
                          : 'text-green-600'
                      }`}
                    >
                      {assignmentConfirmMessage[assignment.rid]}
                    </p>
                  )}
                  {assignmentRejectMessage[assignment.rid] && (
                    <p
                      className={`text-xs mt-2 ${
                        assignmentRejectMessage[assignment.rid].includes('Failed')
                          ? 'text-red-600'
                          : 'text-red-600'
                      }`}
                    >
                      {assignmentRejectMessage[assignment.rid]}
                    </p>
                  )}

                  {/* Warning message */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-600 italic">
                      If you reject an assignment, you will not be given a replacement set at this same time.
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile view (unchanged, hidden on desktop) */}
            <div className="md:hidden space-y-4">
              {shown.map((assignment) => (
                <div
                  key={assignment.rid}
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                >
                  <div className="flex justify-between items-center mb-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        assignment.shift === 'Morning'
                          ? 'bg-yellow-100 text-yellow-800'
                          : assignment.shift === 'Afternoon'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {shiftDateLabel(assignment.shift_date)} {assignment.shift}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        assignment.dj_confirmation_shift
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {assignment.dj_confirmation_shift ? 'Confirmed' : 'Needs Confirmation'}
                    </span>
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-gray-900 font-medium mb-1">Polling Place</p>
                    <p className="text-sm text-gray-700">{assignment.polling_place_address}</p>
                  </div>

                  {/* Time */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-900 font-medium mb-1">Time</p>
                    <p className="text-sm text-gray-700">{assignment.shift_start_time} - {assignment.shift_end_time}</p>
                  </div>
                  {!assignment.dj_confirmation_shift ? (
                    <div className="space-y-2">
                      <button
                      onClick={() => handleConfirmShift(assignment)}
                      disabled={assignmentConfirmLoading[assignment.rid]}
                      className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                        assignmentConfirmLoading[assignment.rid]
                          ? 'bg-gray-400'
                          : 'bg-primary hover:bg-primary-dark'
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                    >
                      {assignmentConfirmLoading[assignment.rid] ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Confirming Set...
                        </>
                      ) : (
                        'Confirm Set'
                      )}
                      </button>
                      <button
                        onClick={() => askToReject(assignment)}
                        disabled={assignmentRejectLoading[assignment.rid]}
                        className={`w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium ${
                          assignmentRejectLoading[assignment.rid]
                            ? 'bg-gray-100 text-gray-400'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                      >
                        {assignmentRejectLoading[assignment.rid] ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Rejecting...
                          </>
                        ) : (
                          "Reject"
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-green-600 font-medium py-2">
                      ✓ Set Confirmed
                    </div>
                  )}
                  {assignmentConfirmMessage[assignment.rid] && (
                    <p
                      className={`mt-2 text-xs text-center ${
                        assignmentConfirmMessage[assignment.rid].includes('Failed')
                          ? 'text-red-600'
                          : 'text-green-600'
                      }`}
                    >
                      {assignmentConfirmMessage[assignment.rid]}
                    </p>
                  )}
                  {assignmentRejectMessage[assignment.rid] && (
                    <p
                      className={`mt-2 text-xs text-center ${
                        assignmentRejectMessage[assignment.rid].includes('Failed')
                          ? 'text-red-600'
                          : 'text-red-600'
                      }`}
                    >
                      {assignmentRejectMessage[assignment.rid]}
                    </p>
                  )}

                  {/* Warning message */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-600 italic text-center">
                      If you reject an assignment, you will not be given a replacement set at this same time.
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No polling place assignments available
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Check back here later to view your assigned polling place assignments and schedule.
            </p>
          </div>
        )}
      </div>

      {rejectingAssignment && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reject-set-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 id="reject-set-title" className="text-lg font-semibold text-gray-900">
              Are you sure?
            </h3>
            <p className="mt-2 text-sm text-gray-700">
              You are about to reject your {rejectingAssignment.shift} set on {rejectingAssignment.shift_date} at {rejectingAssignment.polling_place_address}.
            </p>
            <p className="mt-2 text-sm text-gray-700">
              You can't undo this action, and you will not be given a replacement set at this same time.
            </p>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                autoFocus
                onClick={closeRejectDialog}
                className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                No, keep this set
              </button>
              <button
                onClick={confirmReject}
                className="inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Yes, reject this set
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentsSection;
